import { Flex, SearchInput, Spacing } from '@billy/design-system'
import { Table } from '@shinetools/lumen-react'
import { ChangeEvent, ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FecThirdPartyFilter } from '../../../../enums/fecThirdPartyFilter'
import { FecThirdPartySortKey } from '../../../../enums/fecThirdPartySortKey'
import type { FecThirdPartyMapping } from '../../../../types/fecThirdPartyMapping'
import { filterFecThirdPartyMappings } from '../../../../utils/filterFecThirdPartyMappings'
import {
  getFecThirdPartySortDirection,
  sortFecThirdPartyMappings,
  toggleFecThirdPartySort,
} from '../../../../utils/sortFecThirdPartyMappings'
import { FecImportMappingTableScrollArea } from '../FecImportMappingTableScrollArea'
import { ThirdPartyFilterToggle } from './elements/ThirdPartyFilterToggle'
import { ThirdPartyTableBody } from './elements/ThirdPartyTableBody'
import { ThirdPartyTableHeader } from './elements/ThirdPartyTableHeader'
import * as Styled from './styles'

interface FecImportThirdPartyTableProps {
  hasStatus: boolean
  isLoading: boolean
  thirdPartyMappings: FecThirdPartyMapping[]
}

export const FecImportThirdPartyTable = ({
  hasStatus,
  isLoading,
  thirdPartyMappings,
}: FecImportThirdPartyTableProps): ReactElement => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<FecThirdPartyFilter>(FecThirdPartyFilter.All)
  const [sort, setSort] = useState({ isDescending: false, key: FecThirdPartySortKey.AccountNo })

  const visibleMappings = useMemo(
    () =>
      sortFecThirdPartyMappings({
        hasStatus,
        isDescending: sort.isDescending,
        sortKey: sort.key,
        thirdPartyMappings: filterFecThirdPartyMappings({ filter, searchQuery, thirdPartyMappings }),
      }),
    [filter, hasStatus, searchQuery, sort, thirdPartyMappings],
  )

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }, [])

  const handleSearchClear = useCallback(() => {
    setSearchQuery('')
  }, [])

  const handleSort = useCallback((key: FecThirdPartySortKey) => {
    setSort((currentSort) => toggleFecThirdPartySort(currentSort, key))
  }, [])

  return (
    <Styled.ThirdPartyTableWrapper>
      <Flex alignItems="center" gap={Spacing.S12} width="100%">
        <SearchInput
          fullWidth
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          placeholder={t(
            'settings.organization.accounting.fec_file_import.modal.mapping_step.third_party.search_placeholder',
          )}
          value={searchQuery}
        />
        <ThirdPartyFilterToggle onChange={setFilter} value={filter} />
      </Flex>

      <FecImportMappingTableScrollArea>
        <Table compact>
          <Table.Content>
            <ThirdPartyTableHeader
              accountNoSortDirection={getFecThirdPartySortDirection(sort, FecThirdPartySortKey.AccountNo)}
              onSort={handleSort}
              typeSortDirection={getFecThirdPartySortDirection(sort, FecThirdPartySortKey.Type)}
            />
            <ThirdPartyTableBody hasStatus={hasStatus} isLoading={isLoading} mappings={visibleMappings} />
          </Table.Content>
        </Table>
      </FecImportMappingTableScrollArea>
    </Styled.ThirdPartyTableWrapper>
  )
}
